import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { X, ShoppingCart, Plus, Minus } from 'phosphor-react';
import { motion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import productsService from '@/services/products.service';
import { Product } from '@/types/product.types';
import defaultSnacksImage from '../../../assets/snacks.png';
import './ProductDetail.css';

const ProductDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams<{ productId: string }>();
  const { getCartQuantity, addToCart, updateQuantity } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgSrc, setImgSrc] = useState<string>(defaultSnacksImage);
  const [isDefault, setIsDefault] = useState(false);
  const locationId = location.state?.locationId;

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) return;
      try {
        setIsLoading(true);
        const data = await productsService.getProductById(productId);
        setProduct(data);
        if (data.image) {
          setImgSrc(data.image);
          setIsDefault(false);
        } else {
          setImgSrc(defaultSnacksImage);
          setIsDefault(true);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch product');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const cartQty = productId ? getCartQuantity(productId) : 0;

  const handleAddToCart = () => { if (productId) addToCart(productId); };
  const handleIncrement = () => { if (productId) updateQuantity(productId, cartQty + 1); };
  const handleDecrement = () => { if (productId && cartQty > 0) updateQuantity(productId, cartQty - 1); };
  const handleViewCart = () => navigate('/shop-cart', { state: { location: { id: locationId } } });

  if (isLoading) {
    return (
      <div className="pd">
        <button className="pd__close" onClick={() => navigate(-1)}>
          <X size={16} color="currentColor" weight="bold" />
        </button>
        <div className="pd__skeleton-block pd__skeleton-img" />
        <div className="pd__skeleton-info">
          <div className="pd__skeleton-block pd__skeleton-tag" />
          <div className="pd__skeleton-block pd__skeleton-name" />
          <div className="pd__skeleton-block pd__skeleton-name2" />
          <div className="pd__skeleton-block pd__skeleton-desc" />
          <div className="pd__skeleton-block pd__skeleton-desc2" />
          <div className="pd__skeleton-block pd__skeleton-price" />
        </div>
        <div className="pd__skeleton-actions">
          <div className="pd__skeleton-block pd__skeleton-btn" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pd">
        <button className="pd__close" onClick={() => navigate(-1)}>
          <X size={16} color="currentColor" weight="bold" />
        </button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
          Failed to load product
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="pd"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      {/* Close */}
      <button className="pd__close" onClick={() => navigate(-1)}>
        <X size={16} color="currentColor" weight="bold" />
      </button>

      {/* Image */}
      <div className="pd__img-area">
        <img
          src={imgSrc}
          alt={product.name}
          onError={() => { setImgSrc(defaultSnacksImage); setIsDefault(true); }}
          className={isDefault ? 'pd__img--default' : ''}
        />
      </div>

      {/* Info */}
      <motion.div
        className="pd__info"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1, ease: 'easeOut' }}
      >
        {product.productCollection && (
          <span className="pd__collection">{product.productCollection}</span>
        )}
        <h1 className="pd__name">{product.name}</h1>
        {product.description && (
          <div className="pd__desc" dangerouslySetInnerHTML={{ __html: product.description }} />
        )}
        <p className="pd__price">${product.price.toFixed(2)}</p>
      </motion.div>

      {/* Action bar */}
      <div className="pd__actions" style={{ paddingBottom: `calc(14px + ${cartQty > 0 ? 0 : 0}px + env(safe-area-inset-bottom, 0px))` }}>
        {cartQty > 0 && (
          <div className="pd__cart-pill" onClick={handleViewCart}>
            <ShoppingCart size={18} color="currentColor" weight="bold" />
            <div className="pd__cart-pill-info">
              <span className="pd__cart-pill-count">{cartQty}</span>
            </div>
          </div>
        )}

        {cartQty > 0 ? (
          <div className="pd__qty-bar">
            <button className="pd__qty-btn" onClick={handleDecrement}>
              <Minus size={18} color="currentColor" weight="bold" />
            </button>
            <span className="pd__qty-num">{cartQty}</span>
            <button className="pd__qty-btn" onClick={handleIncrement}>
              <Plus size={18} color="currentColor" weight="bold" />
            </button>
          </div>
        ) : (
          <motion.button
            className="pd__add-btn"
            onClick={handleAddToCart}
            whileTap={{ scale: 0.97 }}
          >
            <Plus size={16} weight="bold" />
            Add to Cart
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default ProductDetail;
